import { useDroppable } from '@dnd-kit/core';
import { Users, CheckSquare, Square, MinusSquare } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@aaas/shared';
import { useTranslation } from 'react-i18next';
import type { JobKanbanColumn } from '../types';
import { KanbanCard } from './KanbanCard';

interface KanbanColumnProps {
  column: JobKanbanColumn;
  jobId?: string;
  selectedCVIds?: Set<string>;
  onToggleSelect?: (cvId: string) => void;
  onToggleSelectAllInRound?: (roundId: string) => void;
  isDraggingMultiple?: boolean;
  getIsDisabled?: (cv: import('../types').JobCVCard) => boolean;
}

export function KanbanColumn({
  column,
  jobId,
  selectedCVIds,
  onToggleSelect,
  onToggleSelectAllInRound,
  isDraggingMultiple,
  getIsDisabled,
}: KanbanColumnProps) {
  const { t } = useTranslation('ats');
  const columnId = column.id;
  const cvCount = column.cvs.length;
  const rejectedCount = column.cvs.filter((cv) => cv.is_rejected).length;

  const { setNodeRef, isOver } = useDroppable({
    id: columnId,
    data: {
      column,
    },
  });

  // Calculate selection state (only count selectable/non-disabled CVs)
  const selectableCVs = getIsDisabled ? column.cvs.filter((cv) => !getIsDisabled(cv)) : column.cvs;
  const selectableCount = selectableCVs.length;
  const cvIdsInColumn = selectableCVs.map((cv) => cv.id);
  const selectedCount = cvIdsInColumn.filter((id) => selectedCVIds?.has(id)).length;
  const allSelected = selectableCount > 0 && selectedCount === selectableCount;
  const someSelected = selectedCount > 0 && selectedCount < selectableCount;

  const handleToggleSelectAll = () => {
    if (onToggleSelectAllInRound) {
      onToggleSelectAllInRound(columnId);
    }
  };

  return (
    <div
      className={`flex-shrink-0 w-72 bg-gray-100 rounded-lg flex flex-col h-full transition-colors ${
        isOver ? 'bg-orange-50 ring-2 ring-orange-400' : ''
      }`}
    >
      {/* Column Header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-500" />
            <h3 className="font-medium text-gray-900 text-sm">{column.name}</h3>
          </div>
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-1.5">
              {/* Select All Button */}
              {selectableCount > 0 && onToggleSelectAllInRound && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleToggleSelectAll}
                      className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200 transition-colors text-gray-600 hover:text-orange-600"
                    >
                      {allSelected ? (
                        <CheckSquare className="h-4 w-4" />
                      ) : someSelected ? (
                        <MinusSquare className="h-4 w-4" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {allSelected
                        ? t('kanban.deselectAll')
                        : someSelected
                          ? t('kanban.selectAllPartial', { selected: selectedCount, total: cvCount })
                          : t('kanban.selectAll')}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-xs font-medium text-gray-700 cursor-pointer">
                    {cvCount}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('kanban.totalCvs', { count: cvCount })}</p>
                </TooltipContent>
              </Tooltip>
              {rejectedCount > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-xs font-medium text-red-700 cursor-pointer">
                      {rejectedCount}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('kanban.rejectedCvs', { count: rejectedCount })}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        </div>
        {column.round_type_label ? (
          <span className="mt-1 inline-block text-xs text-gray-500">{column.round_type_label}</span>
        ) : (
          <span className="mt-1 inline-block text-xs text-gray-500">&nbsp;</span>
        )}
      </div>

      {/* Column Content - Scrollable & Droppable */}
      <div
        ref={setNodeRef}
        className={`flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent ${
          isOver ? 'bg-orange-50/50' : ''
        }`}
      >
        {cvCount === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            {isOver ? t('kanban.dropHere') : t('kanban.noCvs')}
          </div>
        ) : (
          column.cvs.map((cv) => (
            <KanbanCard
              key={cv.id}
              cv={cv}
              columnId={column.id}
              jobId={jobId}
              isSelected={selectedCVIds?.has(cv.id)}
              onToggleSelect={onToggleSelect}
              selectedCVIds={selectedCVIds}
              isDraggingOtherSelected={isDraggingMultiple}
              isDisabled={getIsDisabled?.(cv) ?? false}
            />
          ))
        )}
      </div>
    </div>
  );
}
